import {
  ArrowUp,
  Bot,
  ChevronDown,
  File,
  FileText,
  Mic,
  MoreHorizontal,
  Paperclip,
  Sparkle,
  Square,
} from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { usePopoverPosition } from "../../hooks/usePopoverPosition";
import { Button } from "../Button/Button";
import { IconButton } from "../IconButton/IconButton";
import { SegmentedControl } from "../SegmentedControl/SegmentedControl";
import { Tag } from "../Tag/Tag";
import { Tooltip } from "../Tooltip/Tooltip";
import { ComposerCommandList, commandOptionId } from "./ComposerCommandList";
import { type ComposerCommand, commandKey, commandQuery, matchingCommands, withCommand } from "./slashCommands";

const MAX_TEXTAREA_HEIGHT = 264;

const WAVEFORM_BAR_COUNT = 16;
const WAVEFORM_BARS = Array.from({ length: WAVEFORM_BAR_COUNT }, (_, i) => {
  const distanceFromCenter = Math.abs(i - (WAVEFORM_BAR_COUNT - 1) / 2);
  return { height: 4 + (WAVEFORM_BAR_COUNT / 2 - distanceFromCenter) * 1.6, delay: i * 60 };
});

export interface ComposerAttachment {
  key: string;
  name: string;
  previewUrl?: string;
  mimeType?: string;
  detail?: string;
}

function attachmentIcon(mimeType?: string) {
  if (!mimeType) return File;
  if (
    mimeType.startsWith("application/pdf") ||
    mimeType.includes("word") ||
    mimeType.includes("document") ||
    mimeType.includes("sheet") ||
    mimeType.includes("excel") ||
    mimeType.includes("presentation") ||
    mimeType.includes("powerpoint") ||
    mimeType.startsWith("text/")
  ) {
    return FileText;
  }
  return File;
}

function ComposerMenu({
  isOpen,
  onClose,
  trigger,
  children,
  width,
}: Readonly<{ isOpen: boolean; onClose: () => void; trigger: ReactNode; children: ReactNode; width: string }>) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const { panelRef, style } = usePopoverPosition<HTMLDivElement>({
    isOpen,
    anchorRef,
    placement: "top",
    align: "start",
  });

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as HTMLElement;
      // A nested MenuFlyout portals its own panel straight to the body, so it's a DOM sibling of this
      // panel rather than a descendant. Fall back to role so clicks inside it don't read as "outside".
      if (
        !anchorRef.current?.contains(target) &&
        !panelRef.current?.contains(target) &&
        !target.closest('[role="menu"]')
      ) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, panelRef]);

  const hasAutoFocusedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasAutoFocusedRef.current = false;
      return;
    }
    if (hasAutoFocusedRef.current || style.visibility !== "visible") return;
    panelRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    hasAutoFocusedRef.current = true;
  }, [isOpen, style.visibility, panelRef]);

  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen && wasOpenRef.current) {
      anchorRef.current?.querySelector("button")?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const handleMenuKeyDown = (event: React.KeyboardEvent) => {
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []);
    if (items.length === 0) return;
    const currentIndex = items.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        items[Math.min(currentIndex + 1, items.length - 1)]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        items[Math.max(currentIndex - 1, 0)]?.focus();
        break;
      case "Home":
        event.preventDefault();
        items[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        items[items.length - 1]?.focus();
        break;
    }
  };

  return (
    <div ref={anchorRef} className="relative inline-block">
      {trigger}
      {isOpen &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={style}
            onKeyDown={handleMenuKeyDown}
            // Picking an option closes the menu, like a native select.
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('[role="menuitem"]')) onClose();
            }}
            className={`z-[110] ${width} max-h-[min(28rem,calc(100dvh-1rem))] overflow-y-auto overscroll-contain rounded-xl border border-border-input bg-surface-elevated px-1 py-1 shadow-lg outline-none`}
          >
            {children}
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Every built-in string, so apps can translate them. Templated ones take the value to insert. */
export interface ComposerLabels {
  posture: string;
  ask: string;
  goal: string;
  attach: string;
  assistant: string;
  assistantNamed: (name: string) => string;
  responseMode: string;
  responseModeNamed: (name: string) => string;
  moreOptions: string;
  recordAudio: string;
  stopRecording: string;
  send: string;
  stopGenerating: string;
  /** The command list's accessible name. */
  commands: string;
  noCommandsMatch: (query: string) => string;
}

export const DEFAULT_COMPOSER_LABELS: ComposerLabels = {
  posture: "Posture",
  ask: "Ask",
  goal: "Goal",
  attach: "Attach",
  assistant: "Assistant",
  assistantNamed: (name) => `Assistant: ${name}`,
  responseMode: "Response mode",
  responseModeNamed: (name) => `Response mode: ${name}`,
  moreOptions: "More options",
  recordAudio: "Record audio",
  stopRecording: "Stop recording",
  send: "Send",
  stopGenerating: "Stop",
  commands: "Commands",
  noCommandsMatch: (query) => `No commands match /${query}`,
};

export interface ComposerProps {
  placeholder: string;
  /**
   * Accessible name for the textarea. Placeholder is not a label (it
   * disappears on input and isn't reliably announced), so a multi-line
   * free-text field like this needs one. Defaults to the placeholder for
   * convenience; pass an explicit label when the placeholder is a hint.
   */
  "aria-label"?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onSend?: () => void;

  sendDisabled?: boolean;
  disabled?: boolean;

  /** True while a reply is streaming; swaps the send button for a stop button. */
  generating?: boolean;
  onStop?: () => void;

  posture?: "ask" | "goal";
  defaultPosture?: "ask" | "goal";
  onPostureChange?: (posture: "ask" | "goal") => void;
  /** Set false to hide the Ask/Goal posture control. */
  showPosture?: boolean;

  attachMenu?: ReactNode;

  assistantMenu?: ReactNode;

  assistantLabel?: string;
  assistantIcon?: ReactElement<{ size?: number }>;

  tierMenu?: ReactNode;

  tierLabel?: string;

  moreMenu?: ReactNode;

  onMicClick?: () => void;
  /** True while actively recording; swaps the mic button for a waveform + stop pill. The consumer owns the actual recording/transcription. */
  recording?: boolean;
  /** Disables the mic button (e.g. while a reply streams). The stop button stays enabled so a recording can always be ended. */
  micDisabled?: boolean;

  banner?: ReactNode;

  attachments?: ComposerAttachment[];
  onRemoveAttachment?: (attachment: ComposerAttachment) => void;
  /**
   * Files pasted into the message box, e.g. a screenshot. Not called when the
   * clipboard also has text: a copy from Excel or Word carries a picture of the
   * selection too, and that still pastes as text.
   */
  onFilesPasted?: (files: File[]) => void;

  /**
   * Commands offered when the message starts with `/` (Figma "Slash command list"). Picking one fills in `/name `;
   * the message is sent as typed, so what a command does is up to whoever answers it (an agent's prompt).
   */
  commands?: ComposerCommand[];

  /** Overrides for the built-in strings (see DEFAULT_COMPOSER_LABELS). */
  labels?: Partial<ComposerLabels>;
}

export function Composer({
  placeholder,
  "aria-label": ariaLabel,
  value,
  defaultValue,
  onChange,
  onSend,
  sendDisabled = false,
  disabled = false,
  generating = false,
  onStop,
  posture,
  defaultPosture = "ask",
  onPostureChange,
  attachMenu,
  assistantMenu,
  assistantLabel,
  assistantIcon,
  tierMenu,
  tierLabel,
  moreMenu,
  onMicClick,
  recording = false,
  micDisabled = false,
  banner,
  attachments,
  onRemoveAttachment,
  onFilesPasted,
  labels,
  showPosture = true,
  commands,
}: Readonly<ComposerProps>) {
  const l = { ...DEFAULT_COMPOSER_LABELS, ...labels };
  const isPostureControlled = posture !== undefined;
  const [internalPosture, setInternalPosture] = useState<"ask" | "goal">(defaultPosture);
  const resolvedPosture = isPostureControlled ? posture : internalPosture;

  const [openMenu, setOpenMenu] = useState<"attach" | "assistant" | "tier" | "more" | null>(null);
  const closeMenu = () => setOpenMenu(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pointerDownRef = useRef(false);
  const [focusMethod, setFocusMethod] = useState<"pointer" | "keyboard" | null>(null);
  const isKeyboardFocus = focusMethod === "keyboard";
  const isFocused = focusMethod !== null;
  const textareaId = useId();

  // The command list: open while the message is `/` and part of a command name, until Esc or a pick.
  const [draft, setDraft] = useState(defaultValue ?? "");
  const text = value ?? draft;
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [highlighted, setHighlighted] = useState(0);
  const query = commands?.length ? commandQuery(text) : null;
  const commandsOpen = query !== null && isFocused && dismissed !== text && !disabled;
  const matches = useMemo(() => (query === null ? [] : matchingCommands(commands ?? [], query)), [commands, query]);
  const listId = `${textareaId}-commands`;
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new query starts again at the top.
  useEffect(() => setHighlighted(0), [query]);
  const changeText = (next: string) => {
    setDraft(next);
    onChange?.(next);
  };
  // After a pick, the caret goes to the end as soon as the new text is in the box: a frame later could move it
  // back into whatever the user has typed meanwhile.
  const caretToEnd = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the text changes, to place the caret after a pick.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!caretToEnd.current || !el) return;
    caretToEnd.current = false;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    resize();
  }, [text]);
  const pickCommand = (command: ComposerCommand) => {
    const next = withCommand(command);
    const el = textareaRef.current;
    if (el && value === undefined) el.value = next;
    caretToEnd.current = true;
    changeText(next);
    setDismissed(next);
  };

  const resize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
    el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? "auto" : "hidden";
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately watches value to recalc height on every keystroke, not reading it inside resize.
  useLayoutEffect(resize, [value]);

  // Mic and stop are separate buttons, so the swap drops keyboard focus; hand it to whichever is enabled now.
  const micSlotRef = useRef<HTMLSpanElement>(null);
  const restoreMicFocusRef = useRef(false);
  const handleMicClick = () => {
    restoreMicFocusRef.current = micSlotRef.current?.contains(document.activeElement) ?? false;
    onMicClick?.();
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-runs whenever the mic/stop control swaps or re-enables.
  useEffect(() => {
    if (!restoreMicFocusRef.current) return;
    const active = document.activeElement as HTMLButtonElement | null;
    const lost = !active || active === document.body || (micSlotRef.current?.contains(active) && active.disabled);
    if (!lost) {
      restoreMicFocusRef.current = false;
      return;
    }
    const next = micSlotRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)");
    if (!next) return;
    next.focus();
    restoreMicFocusRef.current = false;
  }, [recording, micDisabled]);

  const handlePointerDown = () => {
    pointerDownRef.current = true;
  };

  const handleFocus = () => {
    setFocusMethod(pointerDownRef.current ? "pointer" : "keyboard");
    pointerDownRef.current = false;
  };

  const handleBlur = () => {
    setFocusMethod(null);
  };

  return (
    <div
      className={[
        "relative rounded-lg border bg-surface-elevated shadow-sm transition-colors",
        isFocused ? "border-border-focus" : "border-border-default hover:border-border-strong",
        isKeyboardFocus ? "input-focus-ring" : "",
        disabled ? "opacity-50" : "",
      ].join(" ")}
    >
      {commandsOpen && (
        <ComposerCommandList
          id={listId}
          matches={matches}
          query={query ?? ""}
          highlighted={highlighted}
          onHighlight={setHighlighted}
          onPick={pickCommand}
          label={l.commands}
          noMatch={l.noCommandsMatch}
        />
      )}
      {banner && <div className="px-3 pt-3">{banner}</div>}
      {attachments && attachments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-3 pt-3">
          {attachments.map((attachment) => {
            const Icon = attachmentIcon(attachment.mimeType);
            return (
              <Tag
                key={attachment.key}
                variant="attachment"
                icon={!attachment.previewUrl && <Icon size={14} className="shrink-0 text-content-tertiary" />}
                previewUrl={attachment.previewUrl}
                detail={attachment.detail}
                onRemove={onRemoveAttachment ? () => onRemoveAttachment(attachment) : undefined}
              >
                {attachment.name}
              </Tag>
            );
          })}
        </div>
      )}
      <textarea
        ref={textareaRef}
        id={textareaId}
        rows={1}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        disabled={disabled}
        value={value}
        defaultValue={defaultValue}
        onChange={(event) => {
          changeText(event.target.value);
          resize();
        }}
        aria-controls={commandsOpen && matches.length > 0 ? listId : undefined}
        aria-activedescendant={commandsOpen && matches.length > 0 ? commandOptionId(listId, highlighted) : undefined}
        aria-autocomplete={commands?.length ? "list" : undefined}
        onKeyDown={(event) => {
          if (commandsOpen) {
            const result = commandKey(event.key, matches, highlighted, event);
            if (result) {
              event.preventDefault();
              if (result.type === "move") setHighlighted(result.index);
              else if (result.type === "pick") pickCommand(result.command);
              else setDismissed(text);
              return;
            }
          }
          // Enter sends, Shift+Enter inserts a newline, the universal
          // multi-line chat composer convention. Guard on sendDisabled the
          // same way the send button's own disabled state does, so an empty
          // or blocked composer doesn't fire onSend.
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            if (!disabled && !sendDisabled) onSend?.();
          }
        }}
        onPaste={(event) => {
          const files = Array.from(event.clipboardData.files);
          if (!onFilesPasted || files.length === 0 || event.clipboardData.getData("text/plain")) return;
          event.preventDefault();
          onFilesPasted(files);
        }}
        onPointerDown={handlePointerDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`min-h-[58px] w-full resize-none bg-transparent px-4 pt-3 pb-2 text-sm outline-hidden placeholder:text-content-tertiary disabled:cursor-not-allowed ${recording ? "text-content-tertiary" : "text-content-primary"}`}
      />
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 px-2.5 pt-0.5 pb-2.5">
        {showPosture && (
          <SegmentedControl
            aria-label={l.posture}
            disabled={disabled}
            options={[
              { value: "ask", label: l.ask },
              { value: "goal", label: l.goal },
            ]}
            value={resolvedPosture}
            onChange={(next: "ask" | "goal") => {
              if (!isPostureControlled) setInternalPosture(next);
              onPostureChange?.(next);
            }}
          />
        )}

        {attachMenu && (
          <ComposerMenu
            isOpen={openMenu === "attach"}
            onClose={closeMenu}
            width="w-64"
            trigger={
              <Tooltip content={l.attach}>
                <IconButton
                  variant="ghost"
                  size="md"
                  aria-label={l.attach}
                  aria-haspopup="menu"
                  aria-expanded={openMenu === "attach"}
                  icon={<Paperclip />}
                  disabled={disabled}
                  onClick={() => setOpenMenu(openMenu === "attach" ? null : "attach")}
                />
              </Tooltip>
            }
          >
            {attachMenu}
          </ComposerMenu>
        )}

        {assistantMenu && (
          <ComposerMenu
            isOpen={openMenu === "assistant"}
            onClose={closeMenu}
            width="w-72"
            trigger={
              <Button
                variant="ghostNeutral"
                size="sm"
                aria-haspopup="menu"
                aria-expanded={openMenu === "assistant"}
                aria-label={assistantLabel ? l.assistantNamed(assistantLabel) : l.assistant}
                leadingIcon={assistantIcon ?? <Bot size={16} />}
                trailingIcon={<ChevronDown size={12} />}
                disabled={disabled}
                onClick={() => setOpenMenu(openMenu === "assistant" ? null : "assistant")}
              >
                {assistantLabel}
              </Button>
            }
          >
            {assistantMenu}
          </ComposerMenu>
        )}

        {tierMenu && (
          <ComposerMenu
            isOpen={openMenu === "tier"}
            onClose={closeMenu}
            width="w-80"
            trigger={
              <span className="max-[639px]:hidden">
                <Button
                  variant="ghostNeutral"
                  size="sm"
                  aria-haspopup="menu"
                  aria-expanded={openMenu === "tier"}
                  aria-label={tierLabel ? l.responseModeNamed(tierLabel) : l.responseMode}
                  leadingIcon={<Sparkle size={16} />}
                  trailingIcon={<ChevronDown size={12} />}
                  disabled={disabled}
                  onClick={() => setOpenMenu(openMenu === "tier" ? null : "tier")}
                >
                  {tierLabel}
                </Button>
              </span>
            }
          >
            {tierMenu}
          </ComposerMenu>
        )}

        {moreMenu && (
          <ComposerMenu
            isOpen={openMenu === "more"}
            onClose={closeMenu}
            width="w-64"
            trigger={
              <Tooltip content={l.moreOptions}>
                <IconButton
                  variant="ghost"
                  size="md"
                  aria-label={l.moreOptions}
                  aria-haspopup="menu"
                  aria-expanded={openMenu === "more"}
                  icon={<MoreHorizontal />}
                  disabled={disabled}
                  onClick={() => setOpenMenu(openMenu === "more" ? null : "more")}
                />
              </Tooltip>
            }
          >
            {moreMenu}
          </ComposerMenu>
        )}

        <div className="flex-1" />

        {onMicClick && (
          <span ref={micSlotRef} className="inline-flex max-[639px]:hidden items-center">
            {recording ? (
              <div className="flex h-8 shrink-0 items-stretch">
                <span
                  aria-hidden="true"
                  className="-mr-2 flex w-[120px] items-center gap-1 rounded-l-lg bg-surface-base pl-2.5 pr-[18px]"
                >
                  {WAVEFORM_BARS.map((bar, index) => (
                    <span
                      // biome-ignore lint/suspicious/noArrayIndexKey: a fixed-length generated decoration, never reordered.
                      key={index}
                      className="w-0.5 shrink-0 animate-waveform-bar rounded-full bg-content-tertiary"
                      style={{ height: bar.height, animationDelay: `${bar.delay}ms` }}
                    />
                  ))}
                </span>
                <Tooltip content={l.stopRecording}>
                  <button
                    type="button"
                    onClick={handleMicClick}
                    disabled={disabled}
                    aria-label={l.stopRecording}
                    className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg bg-brand-primary text-content-on-brand transition-colors btn-focus-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Mic size={16} />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <Tooltip content={l.recordAudio}>
                <IconButton
                  variant="ghost"
                  size="md"
                  aria-label={l.recordAudio}
                  icon={<Mic />}
                  onClick={handleMicClick}
                  disabled={disabled || micDisabled}
                />
              </Tooltip>
            )}
          </span>
        )}
        <Tooltip content={generating ? l.stopGenerating : l.send}>
          <IconButton
            variant="solid"
            size="md"
            aria-label={generating ? l.stopGenerating : l.send}
            icon={generating ? <Square fill="currentColor" /> : <ArrowUp />}
            onClick={generating ? onStop : onSend}
            disabled={generating ? disabled : disabled || sendDisabled}
          />
        </Tooltip>
      </div>
    </div>
  );
}
